export class ApiError extends Error {
  constructor(
    message: string,
    public readonly status: number | null,
    public readonly url: string,
    public readonly detail?: unknown,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

const API_BASE = (import.meta.env.VITE_API_BASE_URL || '/api/v1').replace(/\/$/, '');

export function apiPath(path: string): string {
  return `${API_BASE}/${path.replace(/^\//, '')}`;
}

async function parseBody(response: Response): Promise<unknown> {
  const contentType = response.headers.get('content-type') || '';
  if (response.status === 204) return null;
  if (contentType.includes('application/json')) return response.json();
  return response.text();
}

/**
 * 请求根本没进后端、被中间的网关挡了。
 *
 * 生产实测（0907）：外网入口 pdm.jqdh.net 经雷池 WAF，请求体超过约 50MB 就返回 413；
 * 内网 192.168.6.203:8300 走我们自己的 nginx，限制是 1GB，同样的文件传得上去。
 * 返回 null 表示不是这类错误，按原来的方式报。
 */
function gatewayMessage(status: number, body: unknown): string | null {
  const isHtml = typeof body === 'string' && /<html|<head|<title/i.test(body);
  if (status === 413) {
    return '文件太大，被外网入口挡下了，没有传到系统里。'
      + '外网地址（pdm.jqdh.net）单个文件约 50MB 封顶；'
      + '换内网地址 192.168.6.203:8300 可以传更大的文件，或者先压缩再传。';
  }
  if (!isHtml) return null;
  if (status === 502 || status === 503 || status === 504) {
    return `服务暂时不可用（HTTP ${status}），请求没有到达系统。稍等一会儿重试；一直这样请联系管理员。`;
  }
  return `请求被网关拦下了（HTTP ${status}），没有到达系统。请联系管理员查看网关日志。`;
}

export async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  const url = apiPath(path);
  let response: Response;
  try {
    response = await fetch(url, {
      ...init,
      cache: 'no-store',
      credentials: 'same-origin',
      headers: {
        Accept: 'application/json',
        ...(init.body && !(init.body instanceof FormData) ? { 'Content-Type': 'application/json' } : {}),
        ...init.headers,
      },
    });
  } catch (error) {
    throw new ApiError(`无法连接接口：${error instanceof Error ? error.message : String(error)}`, null, url);
  }
  const body = await parseBody(response);
  if (!response.ok) {
    if (response.status === 401) {
      window.dispatchEvent(new CustomEvent('eqs:unauthorized'));
    }
    /*
      网关挡下来的请求，正文是一整页 nginx 的 HTML 错误页。
      原样甩出去，用户看到的是「<html><head><title>413 Request Entity Too Large</title>…」
      连着十几行 padding 注释（0907 Tony 截图），没人看得懂发生了什么、该怎么办。
      这类错误压根没进后端，说人话比透传原文有用。
    */
    const gatewayHint = gatewayMessage(response.status, body);
    if (gatewayHint) throw new ApiError(gatewayHint, response.status, url, body);

    const serverMessage = typeof body === 'object' && body && 'message' in body
      ? String((body as { message: unknown }).message)
      : typeof body === 'string' && body
        ? body
        : response.statusText;
    throw new ApiError(`接口返回 HTTP ${response.status}：${serverMessage}`, response.status, url, body);
  }
  return body as T;
}

export const api = {
  get: <T>(path: string) => request<T>(path),
  post: <T>(path: string, payload: unknown) => request<T>(path, { method: 'POST', body: JSON.stringify(payload) }),
  patch: <T>(path: string, payload: unknown) => request<T>(path, { method: 'PATCH', body: JSON.stringify(payload) }),
  /*
    DELETE 也收一个可选 payload（0920）：撤下附件要带「为什么撤」，
    塞查询串里会被日志和浏览器历史留下来，放 body 里更合适。
    没有 payload 时保持原样，不给请求加空 body。
  */
  delete: <T>(path: string, payload?: unknown) => request<T>(
    path,
    payload === undefined
      ? { method: 'DELETE' }
      : { method: 'DELETE', body: JSON.stringify(payload) },
  ),
  upload: <T>(path: string, payload: FormData) => request<T>(path, { method: 'POST', body: payload }),
};
